import { useEffect, useState } from "react";
import { useFragment, useLazyLoadQuery, useMutation, useQueryLoader } from "react-relay";
import { moduleRating_ContentSubmissionFragment$key } from "@generated/moduleRating_ContentSubmissionFragment.graphql";
import { moduleRating_createModuleRatingSubmissionMutation } from "@generated/moduleRating_createModuleRatingSubmissionMutation.graphql";
import { moduleRating_Query } from "@generated/moduleRating_Query.graphql";
import {
	CONTENT_SUBMISSION_FRAGMENT,
	CREATE_MODULE_RATING_MUTATION,
	QUERY,
} from "./module-rating.graphql";
import { Container, Header, SuccessMessage } from "./module-rating.styles";
import { ModuleRatingCommentDialogComponent } from "../module-rating-comment-dialog";
import { Star } from "../star";

export const ModuleRating = ({
	contentSubmissionFragmentRef,
}: {
	contentSubmissionFragmentRef: moduleRating_ContentSubmissionFragment$key;
}) => {
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [commitCreate] = useMutation<moduleRating_createModuleRatingSubmissionMutation>(
		CREATE_MODULE_RATING_MUTATION,
	);
	const contentSubmission = useFragment(CONTENT_SUBMISSION_FRAGMENT, contentSubmissionFragmentRef);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, loadQuery] = useQueryLoader<moduleRating_Query>(QUERY);
	const [selectedValue, setSelectedValue] = useState(0);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const [isUpdatingStars, setUpdatingStarts] = useState(false);
	const [isShowingSuccessMessage, setIsShowingSuccessMessage] = useState(false);

	useEffect(() => {
		loadQuery(
			{
				nodeRef: contentSubmission!.learnOpportunity!.root!.id,
			},
			{ fetchPolicy: "network-only" },
		);
	}, [contentSubmission, loadQuery, selectedValue, isUpdatingStars]);

	const query = useLazyLoadQuery<moduleRating_Query>(
		QUERY,
		{
			nodeRef: contentSubmission!.learnOpportunity!.root!.id,
		},
		{ fetchPolicy: "network-only" },
	);
	const moduleRatingForNode = query.ModuleRating.GetModuleRatingOptByNodeAndUserRef;
	const createOnClickHandler = (index: number) => () => {
		if (!contentSubmission.learnOpportunity?.root?.id) return;
		setSelectedValue(index + 1);

		if (index <= 2 && !moduleRatingForNode) {
			setModalIsOpen(true);
		} else {
			commitCreate({
				variables: {
					input: {
						nodeRef: contentSubmission.learnOpportunity?.root?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
						value: index + 1,
						message: "",
					},
				},
				onCompleted() {
					setUpdatingStarts((prevState) => !prevState);
					if (index >= 3) {
						setIsShowingSuccessMessage(true);
						setTimeout(() => setIsShowingSuccessMessage(false), 3000);
					}
				},
			});
		}
	};

	return (
		<Container>
			{isShowingSuccessMessage ? (
				<SuccessMessage>Vielen Dank für Ihre Bewertung</SuccessMessage>
			) : null}
			<div className={"flex flex-column align-items-center"}>
				<Header>Bitte bewerten Sie das Modul</Header>
				<div className={"flex gap-2"}>
					{Array.from({ length: 5 })
						.fill(0)
						.map((_, index) => {
							const isActive =
								index + 1 <=
								(hoveredIndex !== null ? hoveredIndex + 1 : (moduleRatingForNode?.value ?? -1));
							const disableHover = !!moduleRatingForNode;
							return (
								<Star
									key={index}
									index={index}
									onClick={createOnClickHandler(index)}
									isActive={isActive}
									disableHover={disableHover}
									setHoveredIndex={setHoveredIndex}
								/>
							);
						})}
				</div>
			</div>
			<ModuleRatingCommentDialogComponent
				setUpdatingStars={setUpdatingStarts}
				rootNodeId={contentSubmission?.learnOpportunity?.root?.id!} //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
				value={selectedValue}
				isVisible={modalIsOpen}
				setIsVisible={setModalIsOpen}
			/>
		</Container>
	);
};
